/**
 * Embed-Modus (Web-Modul ohne iFrame, sc-3381, ADR 0019): gemeinsamer Schalter für geteilten Code.
 *
 * Der Embed-Einstieg (src/embed/index.tsx) registriert seine Wurzel; der iFrame-Einstieg tut
 * nichts, dort bleibt alles wie bisher. Geteilter Code fragt nur additiv ab ("im Embed anders"),
 * der iFrame-Pfad ist der Standard und bleibt eine Null-Änderung.
 */
let embedRoot: HTMLElement | null = null

/** Bei mehreren Modulen je Seite ist das die *aktive* Wurzel (zuletzt angeklickt, fokussiert oder navigiert);
 *  Login-Modal, Scroll-Brücke und Mollie-Rücksprung richten sich nach ihr. src/embed/index.tsx setzt sie um. */
export function registerEmbedRoot(node: HTMLElement | null): void {
    embedRoot = node
}

export function getEmbedRoot(): HTMLElement | null {
    return embedRoot
}

export function isEmbedMode(): boolean {
    return embedRoot !== null
}

/** Im iFrame heißt der Inhaltscontainer des MainLayouts `content` (Kunden-CSS hängt daran); auf Kundenseiten kollidiert
 *  die ID mit dem Theme (Avada: `#content { width: 71%; float: left }`), deshalb dort `ko-content`. */
export const IFRAME_CONTENT_ID = "content"
export const EMBED_CONTENT_ID = "ko-content"

export function layoutContentId(): string {
    return isEmbedMode() ? EMBED_CONTENT_ID : IFRAME_CONTENT_ID
}

/** Inhaltscontainer des MainLayouts (Container für das Login-Modal); im Embed innerhalb der Wurzel gesucht,
 *  damit nicht `#content` der Kundenseite getroffen wird. */
export function getLayoutContentNode(): HTMLElement | null {
    if (embedRoot) return embedRoot.querySelector<HTMLElement>(`#${EMBED_CONTENT_ID}`) ?? embedRoot
    return document.getElementById(IFRAME_CONTENT_ID)
}

/** Interne AGB-/Datenschutz-Routen (#/agb, #/datenschutz) gibt es nur, wenn die App eingebettet läuft: im iFrame
 *  (bisherige Erkennung) oder im Embed-Modus. Standalone (Direktaufruf der App-URL) bleibt die Anzeige ohne Link. */
export function isEmbeddedForInternalRoutes(): boolean {
    return isEmbedMode() || window.parent !== window || window.location.search.includes("parentUrl")
}

/** Rücksprungbasis für Mollie (ADR 0019 Punkt 6): Im Embed die Kundenseite, auf die der Browser nach der Zahlung
 *  zurück soll; das Backend prüft sie gegen die Allowlist des Tenants und hängt `?ko=/payment-return/…` an.
 *  Bewusst nichts, wo niemand `?ko=` auswertet: im iFrame (App auf dem Tenant-Host, HashRouter) und im Embed mit
 *  `data-routing="hash"` (src/embed/index.tsx). Dort bleibt der bisherige Rücksprung auf die Tenant-Adresse. */
export function embedReturnBaseUrl(): string | undefined {
    return embedRoot && embedRoot.dataset.routing !== "hash" ? window.location.href : undefined
}
